import type { CreateDashboardRequest } from "@/types_api/backend/transport/create_dashboard_transport_request"
import type { DeleteDashboardRequest } from "@/types_api/backend/transport/delete_dashboard_transport_request"
import type { GetDashboardLayoutRequest } from "@/types_api/backend/transport/get_dashboard_layout_request"
import type { DashboardMetadataResource as ListDashboardMetadataResource } from "@/types_api/backend/transport/list_dashboard_metadata_response"
import type {
  DashboardWidgetWriteResource,
  ReplaceDashboardWidgetsRequest,
} from "@/types_api/backend/transport/replace_dashboard_widgets_request"
import type { UpdateDashboardRequest } from "@/types_api/backend/transport/update_dashboard_transport_request"
import {
  normalizeConversationSource,
  toTransportConversationSource,
} from "@/types/conversationSource"

import {
  DATA_SOURCE,
  type DashboardMeta,
  type DashboardWidget,
  type DashboardWidgetConfig,
} from "../types/core"
import type { DashboardDatePreferences } from "../types/dashboardConfig"

type LegacyMonitoringSource = "Monitoring" | "Simulations"

function getLegacyMonitoringForDataSource(
  dataSource?: DashboardWidgetConfig["dataSource"] | null,
): LegacyMonitoringSource | undefined {
  if (dataSource === DATA_SOURCE.UPLOADED) {
    return "Monitoring"
  }

  if (dataSource === DATA_SOURCE.SIMULATED) {
    return "Simulations"
  }

  return undefined
}

function serializeFilterForTransport(
  filters?: DashboardWidgetConfig["filters"],
): Record<string, unknown> | undefined {
  if (!filters) {
    return undefined
  }

  return stripUndefinedProperties({
    metric_ids: filters.metricIds,
    agent_ids: filters.agentIds,
    agent_mutation_ids: filters.agentMutationIds,
    persona_ids: filters.personaIds,
    template_names: filters.templateNames,
    tag_filters: filters.tagFilters,
    test_set_ids: filters.testSetIds,
    test_case_ids: filters.testCaseIds,
    metadata: filters.metadata,
  })
}

function serializeMetricFiltersForTransport(
  metricFilters?: DashboardWidgetConfig["metricFilter"],
): Record<string, unknown>[] | undefined {
  if (!metricFilters) {
    return undefined
  }

  return metricFilters.map((filter) => ({
    metric_id: filter.metricId,
    operator: filter.operator,
    value: filter.value,
    metric_output_type: filter.metricOutputType,
  }))
}

function stripUndefinedProperties(
  value: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const entries = Object.entries(value).filter(
    ([, entryValue]) => entryValue !== undefined,
  )

  return entries.length > 0 ? Object.fromEntries(entries) : undefined
}

function serializeWidgetConfigForTransport(
  config?: DashboardWidgetConfig,
): Record<string, unknown> | undefined {
  if (!config) {
    return undefined
  }

  const conversationSource = normalizeConversationSource(
    config.dataSource ?? config.monitoring,
  )

  const widgetConfig: Record<string, unknown> = {
    title: config.title,
    description: config.description,
    text: config.text,
    metric_id: config.metricId,
    metric_ids: config.metricIds,
    aggregation: config.aggregation,
    visualization_type: config.visualizationType,
    metric_output_type: config.metricOutputType,
    group_by: config.groupByMetadataKey ? undefined : config.groupBy,
    group_by_metadata_key: config.groupByMetadataKey,
    split_by: config.splitBy,
    time_mode: config.timeMode,
    dateRange: config.dateRange,
    bucket_interval: config.bucketInterval,
    stacked: config.stacked,
    grouped: config.grouped,
    show_as_percentage: config.showAsPercentage,
    show_target_zone: config.showTargetZone,
    show_baseline_bands: config.showBaselineBands,
    baseline_id: config.baselineId,
    baseline_sigma_levels: config.baselineSigmaLevels,
    baseline_direction: config.baselineDirection,
    baseline_band_color: config.baselineBandColor,
    x_axis_label: config.xAxisLabel,
    y_axis_label: config.yAxisLabel,
    custom_color_map: config.customColorMap,
    custom_series_names: config.customSeriesNames,
    hidden_series: config.hiddenSeries,
    format: config.format,
    precision: config.precision,
    units: config.units,
    show_count: config.showCount,
    show_range: config.showRange,
    show_std_dev: config.showStdDev,
    show_box_plot: config.showBoxPlot,
    metric_manager: config.metricManager,
    color_scheme: config.colorScheme,
    data_source:
      config.dataSource === DATA_SOURCE.HUMAN_REVIEW
        ? DATA_SOURCE.HUMAN_REVIEW
        : undefined,
    conversation_source: conversationSource
      ? toTransportConversationSource(conversationSource)
      : undefined,
  }

  const legacyMonitoringValue = getLegacyMonitoringForDataSource(
    config.dataSource,
  )
  if (legacyMonitoringValue) {
    widgetConfig.monitoring = legacyMonitoringValue
  } else if (!config.dataSource && config.monitoring) {
    widgetConfig.monitoring = config.monitoring
  }

  const serializedFilters = serializeFilterForTransport(config.filters)
  if (serializedFilters) {
    widgetConfig.filters = serializedFilters
  }

  const serializedMetricFilters = serializeMetricFiltersForTransport(
    config.metricFilter,
  )
  if (serializedMetricFilters) {
    widgetConfig.metric_filter = serializedMetricFilters
  }

  return stripUndefinedProperties(widgetConfig)
}

function serializeDashboardWidgetForTransport(
  widget: DashboardWidget,
): DashboardWidgetWriteResource {
  return {
    id: widget.id,
    type: widget.type as DashboardWidgetWriteResource["type"],
    title: widget.title,
    config: serializeWidgetConfigForTransport(widget.config),
    grid_x: widget.grid_x ?? null,
    grid_y: widget.grid_y ?? null,
    grid_w: widget.grid_w ?? null,
    grid_h: widget.grid_h ?? null,
  }
}

export function serializeDashboardMetadataForTransport(
  dashboard: DashboardMeta,
): ListDashboardMetadataResource {
  return {
    name: `dashboards/${dashboard.id}`,
    display_name: dashboard.display_name || null,
    description: dashboard.description,
    is_default: dashboard.is_default,
    is_favorite: dashboard.is_favorite,
    position: 0,
    config: dashboard.config
      ? {
          date_preferences: dashboard.config.datePreferences
            ? {
                preset: dashboard.config.datePreferences.preset ?? null,
                custom_range:
                  dashboard.config.datePreferences.customRange ?? null,
                relative_config:
                  dashboard.config.datePreferences.relativeConfig ?? null,
              }
            : null,
        }
      : null,
    create_time: dashboard.created_at,
    update_time: dashboard.last_updated_at,
  }
}

export function serializeCreateDashboardRequest(
  displayName: string,
): CreateDashboardRequest {
  return {
    display_name: displayName,
  }
}

export function serializeUpdateDashboardRequest({
  dashboardId,
  displayName,
  isFavorite,
  datePreferences,
}: {
  dashboardId: string
  displayName?: string
  isFavorite?: boolean
  datePreferences?: DashboardDatePreferences | null
}): UpdateDashboardRequest {
  return {
    dashboard_id: dashboardId,
    ...(displayName !== undefined ? { display_name: displayName } : {}),
    ...(isFavorite !== undefined ? { is_favorite: isFavorite } : {}),
    ...(datePreferences !== undefined
      ? {
          date_preferences:
            datePreferences === null
              ? null
              : {
                  preset: datePreferences.preset ?? null,
                  custom_range: datePreferences.customRange ?? null,
                  relative_config: datePreferences.relativeConfig ?? null,
                },
        }
      : {}),
  }
}

export function serializeDeleteDashboardRequest(
  dashboardId: string,
): DeleteDashboardRequest {
  return {
    dashboard_id: dashboardId,
  }
}

export function serializeGetDashboardLayoutRequest(dashboardId?: string) {
  return (
    dashboardId ? { dashboard_id: dashboardId } : {}
  ) satisfies GetDashboardLayoutRequest
}

export function serializeReplaceDashboardWidgetsRequest(
  dashboardId: string,
  widgets: DashboardWidget[],
) {
  return {
    dashboard_id: dashboardId,
    widgets: widgets.map(serializeDashboardWidgetForTransport),
  } satisfies ReplaceDashboardWidgetsRequest
}
